import { _noop } from '@client/new/helpers/general';
import { DndList } from '@design-system/components/dndList/DndList';
import { DnDItem } from '@design-system/components/dndList/DndList.utils';

import {
  ProgramCardRow,
  ProgramsVM,
} from '../../view-model/programs.viewModel.types';

import { ProgramCard } from './ProgramCard';
import {
  ProgramCardDndParent,
  ProgramCardRenderer,
  ProgramCardRendererContext,
} from './ProgramCardRenderer';
import { ProgramsListHeader } from './ProgramsListHeader';
import * as styles from './ProgramCardList.css';

type ProgramCardListProps = {
  rows: Array<ProgramCardRow>;
  isDnDEnabled: boolean;
  onReorder: (ids: Array<number>) => void;
  dispatch: ProgramsVM['dispatch'];
};

const toRowsById = (
  rows: Array<ProgramCardRow>,
): Record<string, ProgramCardRow> =>
  rows.reduce<Record<string, ProgramCardRow>>((acc, row) => {
    acc[String(row.id)] = row;
    return acc;
  }, {});

const toDndRows = (rows: Array<ProgramCardRow>): Array<ProgramCardDndParent> =>
  rows.map((row) => ({
    id: String(row.id),
    items: [] as Array<DnDItem>,
  }));

export const ProgramCardList = ({
  rows,
  isDnDEnabled,
  onReorder,
  dispatch,
}: ProgramCardListProps) => {
  if (!rows.length) return null;

  const rowsById = toRowsById(rows);

  if (!isDnDEnabled) {
    return (
      <div className={styles.wrapper} data-testid="program-card-list-static">
        <ProgramsListHeader reorderMode={false} />
        {rows.map((row) => (
          <ProgramCard key={row.id} row={row} dispatch={dispatch} />
        ))}
      </div>
    );
  }

  return (
    <div className={styles.wrapper} data-testid="program-card-list-dnd">
      <ProgramsListHeader reorderMode={true} />
      <ProgramCardRendererContext.Provider value={{ rowsById, dispatch }}>
        <DndList
          items={toDndRows(rows)}
          onChange={(nextRows) => {
            const nextIds = nextRows
              .map((row) => rowsById[row.id]?.id)
              .filter((id): id is number => typeof id === 'number');
            onReorder(nextIds);
          }}
          renderParent={ProgramCardRenderer}
          renderChild={_noop}
        />
      </ProgramCardRendererContext.Provider>
    </div>
  );
};
